import { UI, HStack, Stack, VStack } from '@genial/components';
import { Spacing } from '@genial/design-tokens';
import { useBreakpointValue } from '@genial/utils';
import React from 'react';

import { useBatchTicketAssetsContext } from '../../contexts';
import { useCustomersAdded } from '../../hooks';
import { DynamicButton } from '../DynamicButton';

import { AssetCaptions } from './components/AssetCaptions';
import { AssetHandlers } from './components/AssetHandlers';
import { AssetList } from './components/AssetList';
import { AssetNotices } from './components/AssetNotices';
import { AssetTabs } from './components/AssetTabs';
import { OperationSwitch } from './components/OperationSwitch';
import { SearchTerm } from './components/SearchTerm';
import { GlobalAssetContextReceivedData, GlobalAssetProvider } from './contexts';
import { generateInitialTabs } from './contexts/constants';

export interface DynamicAssetProps {
  infoGetter: GlobalAssetContextReceivedData['infoGetter'];
  initialTabs: GlobalAssetContextReceivedData['initialTabs'];
  isBatchTicket?: boolean;
  accountId?: number;
  document?: string;
  accountNumber?: number;
  checkingAccountNumber?: number;
  multipleAccountsIds?: number[];
}

export const DynamicAsset: React.FC<DynamicAssetProps> = ({
  infoGetter,
  initialTabs,
  accountId,
  accountNumber = 0,
  checkingAccountNumber,
  document,
  multipleAccountsIds,
  isBatchTicket = true,
}) => {
  const isDesktop = useBreakpointValue({ base: false, sm: false, lg: true });
  const isTablet = useBreakpointValue({ base: false, sm: true, lg: false });
  const isMobile = useBreakpointValue({ base: true, sm: false, lg: false });

  const renderContent = () => {
    if (isDesktop) {
      return (
        <VStack w="full">
          <Stack pb={Spacing['stack.xxxs']}>
            <AssetTabs />
          </Stack>
          <HStack w="full" justifyContent="space-between" alignItems="center">
            <Stack w="40%">
              <SearchTerm />
            </Stack>
            <OperationSwitch />
          </HStack>
          <HStack w="full">
            <AssetNotices />
          </HStack>
          <AssetHandlers />

          <AssetCaptions />
          <AssetList />
        </VStack>
      );
    }

    if (isTablet) {
      return (
        <VStack w="full">
          <AssetTabs />
          <HStack w="full">
            <AssetNotices />
          </HStack>
          <HStack w="full" justifyContent="space-between" alignItems="center" pt="stack.xxs">
            <Stack w="40%">
              <SearchTerm />
            </Stack>
            <OperationSwitch />
          </HStack>
          <AssetHandlers />
          <AssetCaptions />
          <AssetList />
        </VStack>
      );
    }

    return (
      <VStack w="full">
        <Stack pb={Spacing['stack.xxxs']}>
          <AssetTabs />
        </Stack>
        <HStack w="full" justifyContent="space-between" alignItems="center">
          <SearchTerm />
          <AssetNotices />
        </HStack>
        <OperationSwitch />
        <AssetHandlers />
        <AssetList />
      </VStack>
    );
  };

  return (
    <GlobalAssetProvider
      isDesktop={isDesktop}
      isTablet={isTablet}
      isMobile={isMobile}
      accountNumber={accountNumber}
      checkingAccountNumber={checkingAccountNumber}
      multipleAccountsIds={multipleAccountsIds}
      infoGetter={infoGetter}
      initialTabs={initialTabs}
      isBatchTicket={isBatchTicket}
      document={document}
      accountId={accountId}>
      {renderContent()}
    </GlobalAssetProvider>
  );
};

export const DynamicAssetWrapper = () => {
  const { customerCartItems } = useCustomersAdded();
  const { isProductSelected, isProductLoading } = useBatchTicketAssetsContext();

  const productsEnabled = generateInitialTabs(true);

  const customersAccountIdsSelected = customerCartItems.map((item) => item.accountId);

  if (!productsEnabled.length) {
    return (
      <Stack py="stack.huge">
        <UI.ErrorBoundary
          errorIconOrIllustration="NotEmptyIllustration"
          errorIconColor="brand.tertiary.principal"
          errorTitle="Nenhum produto disponível no momento"
          errorMessage="Caso tenha dúvidas, entre em contato com time de middle pelo e-mail middleb2b@genial.com.vc"
        />
      </Stack>
    );
  }

  return (
    <DynamicAsset
      multipleAccountsIds={customersAccountIdsSelected}
      initialTabs={productsEnabled}
      infoGetter={(productType, offer, idSelector) => {
        return {
          Button: DynamicButton,
          isSelected: isProductSelected(productType, offer, idSelector),
          forceLoading: isProductLoading(productType, offer, idSelector),
        };
      }}
    />
  );
};
